import { Injectable, computed, inject, signal } from '@angular/core';
import { CustomerSearchResult, CustomerService } from '../../../_services/customer.service';
import { ShippingConfigPayload, ShippingConfigService } from '../../../_services/shipping-config.service';
import {
  ArticleDisposition,
  PackageSpeed,
  PickpointStation,
  PickpointStationShort,
  ShipmentMethod,
  ShippingConfig,
  ShippingConfigContext,
  ShippingConfigFormData,
  ShippingConfigType,
} from '../../../_models/shipping-config.interface';
import {
  SHIPPING_TYPES,
  defaultDispositionForType,
  isPlaceholderTechnician,
  shippingTypeDisabledReason,
  showTechnicianSelectorFor,
  visibleShipmentMethodsFor,
} from '../../../_models/shipping-config-rules';
import {
  defaultPickupDate,
  scheduleDefaultsFor,
  toDatetimeLocal,
} from '../../../_models/shipping-schedule-defaults';
import { pickupOpeningHint, pickupValidityError } from '../../../_models/shipping-pickup-validation';
import { POSITIONS_BY_TYPE, POSITION_META, PositionId, PositionMeta } from './shipping-target-positions';
import {
  ShippingTargetOptionsService,
  TechnicianOption,
  WarehouseOption,
} from './shipping-target-options.service';

/** Startwerte, die der Dialog von der Versand-Matrix bekommt. */
export interface ShippingTargetDialogInit {
  operationId: number;
  /** Bestehende Config = Bearbeiten, null = Anlegen. */
  config: ShippingConfig | null;
  /** Bereits geladener Kontext — der Dialog wartet nie selbst darauf. */
  context: ShippingConfigContext;
  ticketStoreId?: number;
  ticketLat?: number;
  ticketLng?: number;
  referenceDate?: string;
  projectId?: number;
  operationTechnicianId?: number;
  operationTechnicianName?: string;
  /** Sind dieser Config bereits Staging-Artikel zugeordnet? */
  stagingArticlesAssigned?: boolean;
  /** Hat die Config bereits vorbereitete/versandte Mengen? Sperrt den Typwechsel. */
  hasCommitted?: boolean;
  /** Nur diese Versandtypen anbieten (Staging braucht deckende Typen). */
  coveringTypesOnly?: boolean;
}

/** Eine Position der Checkliste, aufbereitet fürs Template. */
export interface PositionView {
  id: PositionId;
  index: number;
  meta: PositionMeta;
  valid: boolean;
  /** Wert wird gerade geladen → neutral statt rot anzeigen. */
  pending: boolean;
}

/**
 * Gesamter Zustand des geführten Versandziel-Dialogs. Wird von der Dialog-Hülle
 * über `providers` bereitgestellt, sodass alle Unterkomponenten ihn per
 * `inject()` teilen — ohne Input-/Output-Ketten.
 */
@Injectable()
export class ShippingTargetDialogState {
  private configService = inject(ShippingConfigService);
  private customerService = inject(CustomerService);
  private optionsService = inject(ShippingTargetOptionsService);

  // --- Kontext aus der Matrix ---
  readonly operationId = signal<number>(0);
  readonly initialConfig = signal<ShippingConfig | null>(null);
  readonly context = signal<ShippingConfigContext | null>(null);
  readonly ticketStoreId = signal<number | undefined>(undefined);
  readonly ticketLat = signal<number | undefined>(undefined);
  readonly ticketLng = signal<number | undefined>(undefined);
  readonly referenceDate = signal<string | undefined>(undefined);
  readonly projectId = signal<number | undefined>(undefined);
  readonly operationTechnicianId = signal<number | undefined>(undefined);
  readonly operationTechnicianName = signal<string | undefined>(undefined);
  readonly stagingArticlesAssigned = signal<boolean>(false);
  readonly hasCommitted = signal<boolean>(false);
  readonly coveringTypesOnly = signal<boolean>(false);

  // --- Navigation ---
  readonly mode = computed<'create' | 'edit'>(() => (this.initialConfig() ? 'edit' : 'create'));
  readonly page = signal<'type' | 'positions'>('type');

  // --- Formular-Felder ---
  readonly selectedType = signal<ShippingConfigType>('pickpoint');
  readonly selectedStation = signal<PickpointStationShort | null>(null);
  readonly shipmentMethod = signal<ShipmentMethod>('parcel');
  readonly packageSpeed = signal<PackageSpeed | null>('standard');
  readonly articleDisposition = signal<ArticleDisposition>('technician_transfer');
  readonly targetUserFromOperation = signal<boolean>(true);
  readonly targetUserId = signal<number | null>(null);
  readonly targetWarehouseId = signal<number | null>(null);
  readonly technicianShipTarget = signal<'private' | 'warehouse'>('warehouse');
  readonly pmCode = signal<string>('');
  readonly pmCodeAutoLoaded = signal<boolean>(false);
  readonly pickupDate = signal<string>('');
  readonly targetCustomerId = signal<number | null>(null);
  readonly targetCustomerName = signal<string>('');
  readonly targetCustomerHasReturn = signal<boolean>(false);

  // --- Auswahllisten ---
  readonly technicians = signal<TechnicianOption[]>([]);
  readonly warehouses = signal<WarehouseOption[]>([]);
  readonly loadingWarehouses = signal<boolean>(false);
  readonly customerResults = signal<CustomerSearchResult[]>([]);
  readonly showCustomerDropdown = signal<boolean>(false);

  // --- Speicher-Zustand ---
  readonly saving = signal<boolean>(false);
  readonly errorMessage = signal<string | null>(null);
  readonly showMapPicker = signal<boolean>(false);

  /**
   * Positionen, deren Wert noch geladen wird. Ohne dieses Set würde z.B. der
   * PM-Code beim Öffnen kurz rot aufblitzen, bevor der System-Code da ist.
   */
  private readonly pendingPositions = signal<ReadonlySet<PositionId>>(new Set());

  /**
   * Woher die Termine kommen: 'initial' spiegelt beim Bearbeiten den DB-Stand
   * unverändert zurück, 'defaults' berechnet sie neu (Anlegen und Typwechsel).
   */
  private readonly scheduleSource = signal<'initial' | 'defaults'>('defaults');

  // ===== Initialisierung =====

  init(data: ShippingTargetDialogInit): void {
    this.operationId.set(data.operationId);
    this.initialConfig.set(data.config);
    this.context.set(data.context);
    this.ticketStoreId.set(data.ticketStoreId);
    this.ticketLat.set(data.ticketLat);
    this.ticketLng.set(data.ticketLng);
    this.referenceDate.set(data.referenceDate);
    this.projectId.set(data.projectId);
    this.operationTechnicianId.set(data.operationTechnicianId);
    this.operationTechnicianName.set(data.operationTechnicianName);
    this.stagingArticlesAssigned.set(data.stagingArticlesAssigned ?? false);
    this.hasCommitted.set(data.hasCommitted ?? false);
    this.coveringTypesOnly.set(data.coveringTypesOnly ?? false);
    this.errorMessage.set(null);

    if (data.config) {
      this.applyConfig(data.config);
      this.scheduleSource.set('initial');
      this.page.set('positions');
    } else {
      this.applyDefaults();
      this.scheduleSource.set('defaults');
      this.page.set('type');
    }
    this.applyProjectCustomerDefault();

    void this.loadTechnicians();
    const userId = this.targetUserId();
    if (userId != null) void this.loadWarehouses(userId);
    if (this.selectedType() === 'pickpoint' && !this.pmCode().trim()) void this.loadMasterPmCode();
  }

  /** Bestehende Config in die Formular-Felder übernehmen (exakter DB-Stand). */
  private applyConfig(c: ShippingConfig): void {
    this.selectedType.set(c.shipping_type);
    this.selectedStation.set(c.pickpoint_station ?? null);
    this.shipmentMethod.set(c.shipment_method ?? 'parcel');
    this.packageSpeed.set(c.shipment_method === 'parcel' ? (c.package_speed ?? 'standard') : null);
    this.articleDisposition.set(c.article_disposition ?? defaultDispositionForType(c.shipping_type));
    this.targetUserFromOperation.set(c.target_user_from_operation ?? false);
    this.targetUserId.set(c.target_user_id ?? null);
    this.targetWarehouseId.set(c.target_warehouse_id ?? null);
    this.technicianShipTarget.set(c.technician_ship_target ?? 'warehouse');
    this.pmCode.set(c.pm_code ?? '');
    this.pmCodeAutoLoaded.set(false);
    const pickupFromDb = c.pickup_date ? toDatetimeLocal(c.pickup_date) : '';
    this.pickupDate.set(
      pickupFromDb || (c.shipping_type === 'pickpoint' ? defaultPickupDate(this.referenceDate()) : ''),
    );
    this.targetCustomerId.set(c.target_customer_id ?? null);
    this.targetCustomerName.set(c.target_customer_name ?? '');
    // Beim Speichern gegen die Rücksendeadresse validiert → als vorhanden behandeln.
    this.targetCustomerHasReturn.set(c.target_customer_id != null);
  }

  /** Startwerte fürs Anlegen — inklusive Auftragstechniker-Vorbelegung. */
  private applyDefaults(): void {
    const firstType = this.selectableTypes()[0]?.value ?? 'pickpoint';
    this.selectedType.set(firstType);
    this.selectedStation.set(null);
    this.shipmentMethod.set('parcel');
    this.packageSpeed.set('standard');
    this.articleDisposition.set(defaultDispositionForType(firstType));
    // Regel als Standard: der Ziel-Techniker kommt dynamisch vom Auftrag.
    this.targetUserFromOperation.set(true);
    this.targetUserId.set(null);
    this.targetWarehouseId.set(null);
    this.technicianShipTarget.set('warehouse');
    this.pmCode.set('');
    this.pmCodeAutoLoaded.set(false);
    this.pickupDate.set(defaultPickupDate(this.referenceDate()));
    this.clearCustomer();
    this.applyInternalStoreTechnician();
  }

  /**
   * Kunden-Rückversand ohne eigene Auswahl geht ohnehin an den Projekt-Kunden.
   * Den setzen wir direkt, damit der Dialog sofort zeigt, ob dort eine
   * Rücksendeadresse hinterlegt ist — statt eines leeren Suchfelds mit
   * Fußnote. Bei internen Projekten bleibt die bewusste Auswahl Pflicht.
   */
  private applyProjectCustomerDefault(): void {
    if (this.selectedType() !== 'customer_shipping') return;
    if (this.targetCustomerId() != null) return;

    const context = this.context();
    if (context?.project_is_internal) return;

    const customerId = context?.customer_id;
    if (customerId == null) return;

    this.targetCustomerId.set(customerId);
    this.targetCustomerName.set(context?.customer_name ?? '');
    this.targetCustomerHasReturn.set(context?.customer_has_return_address === true);
  }

  /**
   * Interner Materialversand: Ist über die Ticketfiliale ein Techniker
   * aufgelöst, wird er als FESTER Ziel-Techniker vorbelegt (statt der Regel).
   */
  private applyInternalStoreTechnician(): void {
    const id = this.context()?.internal_store_technician_id;
    const name = this.context()?.internal_store_technician_name;
    if (id == null) return;

    if (name && !this.technicians().some((t) => t.id === id)) {
      this.technicians.set([...this.technicians(), { id, name }].sort((a, b) => a.name.localeCompare(b.name)));
    }
    this.targetUserFromOperation.set(false);
    this.setTechnician(id);
  }

  // ===== Versandtypen =====

  /** Kacheln der Typ-Auswahl, bei Staging-Bedarf ohne Kunden-Rückversand. */
  readonly selectableTypes = computed(() =>
    this.coveringTypesOnly()
      ? SHIPPING_TYPES.filter((t) => t.value !== 'customer_shipping')
      : SHIPPING_TYPES,
  );

  typeDisabledReason(type: ShippingConfigType): string | null {
    return shippingTypeDisabledReason(type, {
      ticketStoreId: this.ticketStoreId(),
      stagingArticlesAssigned: this.stagingArticlesAssigned(),
    });
  }

  /** Versandtyp wechseln: unzulässige Werte zurücksetzen, Termine neu ableiten. */
  setType(type: ShippingConfigType): void {
    if (this.typeDisabledReason(type) !== null || this.readOnly()) return;
    const previous = this.selectedType();
    this.selectedType.set(type);

    if (type === 'pickpoint') {
      const method = this.shipmentMethod();
      if (method !== 'parcel' && method !== 'courier') this.setShipmentMethod('parcel');
      if (!this.pickupDate()) this.pickupDate.set(defaultPickupDate(this.referenceDate()));
      if (!this.pmCode().trim()) void this.loadMasterPmCode();
    }
    this.articleDisposition.set(defaultDispositionForType(type, this.articleDisposition()));
    this.applyProjectCustomerDefault();

    // Nur bei echtem Wechsel: die Termine des alten Typs passen nicht mehr.
    if (previous !== type) this.scheduleSource.set('defaults');
    this.page.set('positions');
  }

  setShipmentMethod(method: ShipmentMethod): void {
    this.shipmentMethod.set(method);
    if (method === 'parcel') {
      if (this.packageSpeed() == null) this.packageSpeed.set('standard');
    } else {
      this.packageSpeed.set(null);
    }
  }

  readonly visibleShipmentMethods = computed(() => visibleShipmentMethodsFor(this.selectedType()));

  // ===== Ziel-Techniker =====

  readonly showTechnicianSelector = computed(() =>
    showTechnicianSelectorFor(this.selectedType(), this.articleDisposition()),
  );

  /** Auftragstechniker ist ein Platzhalter → im Regel-Modus löst sich keiner auf. */
  readonly operationTechnicianIsPlaceholder = computed(() =>
    isPlaceholderTechnician(this.operationTechnicianId()),
  );

  setTargetUserFromOperation(on: boolean): void {
    this.targetUserFromOperation.set(on);
    if (on) {
      this.targetUserId.set(null);
      this.targetWarehouseId.set(null);
      this.warehouses.set([]);
    }
  }

  setTechnician(userId: number | null): void {
    this.targetUserId.set(userId);
    this.targetWarehouseId.set(null);
    if (userId == null) {
      this.warehouses.set([]);
      return;
    }
    void this.loadWarehouses(userId);
  }

  // ===== PickPoint =====

  setStation(station: PickpointStation | null): void {
    if (!station) return;
    this.selectedStation.set({
      id: station.id,
      pp_id: station.pp_id,
      name: station.name,
      address_zip: station.address_zip,
      address_city: station.address_city,
      active: station.active,
      // Für die Abholzeit-Prüfung weiterreichen.
      openings: station.openings,
      activation_date: station.activation_date,
      cancellation_date: station.cancellation_date,
    });
    this.showMapPicker.set(false);
  }

  readonly canOpenMap = computed(() => {
    const lat = this.ticketLat();
    const lng = this.ticketLng();
    return lat != null && lng != null && !Number.isNaN(lat) && !Number.isNaN(lng);
  });

  readonly pickupError = computed(() =>
    this.selectedType() === 'pickpoint'
      ? pickupValidityError(this.selectedStation(), this.pickupDate())
      : null,
  );

  readonly pickupHint = computed(() =>
    this.selectedType() === 'pickpoint'
      ? pickupOpeningHint(this.selectedStation(), this.pickupDate())
      : null,
  );

  // ===== Ziel-Kunde =====

  searchCustomers(term: string): void {
    const query = term.trim();
    if (query.length < 2) {
      this.customerResults.set([]);
      this.showCustomerDropdown.set(false);
      return;
    }
    this.customerService.searchCustomers(query).subscribe({
      next: (results) => {
        this.customerResults.set(results);
        this.showCustomerDropdown.set(true);
      },
      error: (err) => console.error('Kundensuche fehlgeschlagen', err),
    });
  }

  selectCustomer(customer: CustomerSearchResult): void {
    this.targetCustomerId.set(customer.id);
    this.targetCustomerName.set(customer.name);
    this.targetCustomerHasReturn.set(customer.has_return_address);
    this.customerResults.set([]);
    this.showCustomerDropdown.set(false);
  }

  clearCustomer(): void {
    this.targetCustomerId.set(null);
    this.targetCustomerName.set('');
    this.targetCustomerHasReturn.set(false);
  }

  // ===== Positionen =====

  /** Gültigkeit je Position — einzige Quelle für Rahmenfarbe UND Speicherbarkeit. */
  private positionValid(id: PositionId): boolean {
    switch (id) {
      case 'station':
        return this.selectedStation() != null;
      case 'method':
        return this.shipmentMethod() != null
          && (this.shipmentMethod() !== 'parcel' || this.packageSpeed() != null);
      case 'technician':
        return (this.targetUserFromOperation() || this.targetUserId() != null)
          && (this.selectedType() !== 'pickpoint' || this.pickupError() == null);
      case 'pm_code':
        return this.pmCode().trim().length > 0;
      case 'disposition':
        return this.articleDisposition() === 'consumed'
          || this.targetUserFromOperation()
          || this.targetUserId() != null;
      case 'ship_address':
        return this.technicianShipTarget() != null;
      case 'customer':
        return this.customerPositionValid();
    }
  }

  /**
   * Kunden-Rückversand: mit gewähltem Ziel-Kunden zählt dessen Rücksendeadresse,
   * ohne Auswahl die des Projekt-Kunden — bei internen Projekten ist die
   * Auswahl Pflicht.
   */
  private customerPositionValid(): boolean {
    if (this.stagingArticlesAssigned()) return false;
    if (this.targetCustomerId() != null) return this.targetCustomerHasReturn();
    if (this.context()?.project_is_internal) return false;
    return this.context()?.customer_has_return_address === true;
  }

  readonly positions = computed<PositionView[]>(() => {
    const pending = this.pendingPositions();
    return POSITIONS_BY_TYPE[this.selectedType()].map((id, index) => ({
      id,
      index: index + 1,
      meta: POSITION_META[id],
      valid: this.positionValid(id),
      pending: pending.has(id),
    }));
  });

  /** Sind dieser Config alle Positionen ausgefüllt? */
  readonly canSave = computed(() => !this.readOnly() && this.positions().every((p) => p.valid));

  // ===== Sperren =====

  /**
   * Sobald ein Kanal versandt ist, lehnt das Backend jede Änderung der
   * Ziel-Attribute ab (der Versandtyp beschreibt die rausgegangene Sendung).
   * Der Dialog zeigt die Config dann nur noch an; die Termine bleiben über den
   * Versandzeitpunkt-Dialog änderbar.
   */
  readonly readOnly = computed(() => {
    const c = this.initialConfig();
    return !!c && (!!c.versand_shipped || !!c.staging_shipped);
  });

  /** Typwechsel nur, solange nichts vorbereitet oder versandt ist. */
  readonly canChangeType = computed(() =>
    this.mode() === 'create' || (!this.readOnly() && !this.hasCommitted()),
  );

  // ===== Laden =====

  private async loadTechnicians(): Promise<void> {
    try {
      const list = await this.optionsService.technicians(this.projectId());
      // Einen über die Ticketfiliale aufgelösten Techniker nicht verlieren —
      // er ist evtl. kein Projekt-Techniker und fehlt in der normalen Liste.
      const matched = this.technicians().filter((t) => !list.some((l) => l.id === t.id));
      this.technicians.set([...list, ...matched].sort((a, b) => a.name.localeCompare(b.name)));
    } catch {
      // Ohne Liste bleibt nur die Regel „vom Auftrag" — kein harter Fehler.
    }
  }

  private async loadWarehouses(userId: number): Promise<void> {
    this.loadingWarehouses.set(true);
    try {
      const list = await this.optionsService.warehouses(userId);
      this.warehouses.set(list);
      const current = this.targetWarehouseId();
      if (current != null && !list.some((w) => w.id === current)) {
        this.targetWarehouseId.set(list[0]?.id ?? null);
      } else if (current == null && list.length === 1) {
        this.targetWarehouseId.set(list[0].id);
      }
    } catch {
      this.warehouses.set([]);
    } finally {
      this.loadingWarehouses.set(false);
    }
  }

  private async loadMasterPmCode(): Promise<void> {
    this.markPending('pm_code', true);
    try {
      const code = await this.configService.getMasterPmCode();
      if (code && !this.pmCode().trim()) {
        this.pmCode.set(code);
        this.pmCodeAutoLoaded.set(true);
      }
    } finally {
      this.markPending('pm_code', false);
    }
  }

  private markPending(id: PositionId, pending: boolean): void {
    const next = new Set(this.pendingPositions());
    if (pending) next.add(id);
    else next.delete(id);
    this.pendingPositions.set(next);
  }

  // ===== Speichern =====

  /**
   * Termine: beim Bearbeiten den DB-Stand unverändert zurückspiegeln. Das ist
   * Pflicht, weil der Sanitizer im Backend das Versandfenster verwirft, sobald
   * die Spätestens-Regel im Payload fehlt — ein Ziel-Edit würde es sonst still
   * löschen. Beim Anlegen (und nach einem Typwechsel) gelten die Defaults der
   * Auftragsgruppe, ohne dass der Dialog danach fragt.
   */
  private scheduleFields(): Pick<
    ShippingConfigFormData,
    'shipping_date' | 'shipping_date_offset_days' | 'earliest_shipping_offset_days'
    | 'staging_ship_ready_at' | 'staging_ship_offset_days'
  > {
    const config = this.initialConfig();
    if (config && this.scheduleSource() === 'initial') {
      return {
        shipping_date: config.shipping_date ?? null,
        shipping_date_offset_days: config.shipping_date_offset_days ?? null,
        earliest_shipping_offset_days: config.earliest_shipping_offset_days ?? null,
        staging_ship_ready_at: config.staging_ship_ready_at
          ? toDatetimeLocal(config.staging_ship_ready_at)
          : null,
        staging_ship_offset_days: config.staging_ship_offset_days ?? null,
      };
    }

    const defaults = scheduleDefaultsFor(this.selectedType(), {
      context: this.context(),
      referenceDate: this.referenceDate(),
      pickupDate: this.pickupDate(),
    });
    const useWindow = defaults.shippingDateMode === 'window';
    const useOffset = defaults.shippingDateMode === 'offset' || useWindow;
    return {
      shipping_date: useOffset ? null : defaults.shippingDate || null,
      shipping_date_offset_days: useOffset ? defaults.shippingOffsetDays : null,
      earliest_shipping_offset_days: useWindow ? defaults.earliestOffsetDays : null,
      staging_ship_ready_at: defaults.stagingShipMode === 'fixed'
        ? defaults.stagingShipReadyAt || null
        : null,
      staging_ship_offset_days: defaults.stagingShipMode === 'offset'
        ? defaults.stagingOffsetDays
        : null,
    };
  }

  /**
   * Payload fürs Backend. `notes` fehlt bewusst: abwesende Keys fasst der
   * Controller nicht an, sodass die Lageristen-Notiz aus der Matrix erhalten
   * bleibt.
   */
  buildFormData(): ShippingConfigFormData {
    const type = this.selectedType();
    const disposition = defaultDispositionForType(type, this.articleDisposition());
    const ruleTechnician = showTechnicianSelectorFor(type, disposition) && this.targetUserFromOperation();

    const data: ShippingConfigFormData = {
      shipping_type: type,
      shipment_method: this.shipmentMethod(),
      package_speed: this.shipmentMethod() === 'parcel' ? this.packageSpeed() : null,
      article_disposition: disposition,
      pm_code: type === 'pickpoint' ? this.pmCode().trim() : null,
      pickup_date: type === 'pickpoint' ? this.pickupDate() || null : null,
      ...this.scheduleFields(),
    };

    switch (type) {
      case 'pickpoint':
        data.pickpoint_station_id = this.selectedStation()?.id ?? null;
        data.target_user_from_operation = this.targetUserFromOperation();
        data.target_user_id = ruleTechnician ? null : this.targetUserId();
        data.target_warehouse_id = ruleTechnician ? null : this.targetWarehouseId();
        break;
      case 'store_shipping':
        if (disposition === 'consumed') {
          data.target_user_id = null;
          data.target_warehouse_id = null;
        } else {
          data.target_user_from_operation = this.targetUserFromOperation();
          data.target_user_id = ruleTechnician ? null : this.targetUserId();
          data.target_warehouse_id = ruleTechnician ? null : this.targetWarehouseId();
        }
        break;
      case 'technician_shipping':
        data.target_user_from_operation = this.targetUserFromOperation();
        data.target_user_id = ruleTechnician ? null : this.targetUserId();
        data.target_warehouse_id = ruleTechnician ? null : this.targetWarehouseId();
        data.technician_ship_target = this.technicianShipTarget();
        break;
      case 'customer_shipping':
        // Leer = Rückversand an den Projekt-Kunden (Backend-Fallback).
        data.target_customer_id = this.targetCustomerId();
        break;
    }

    return data;
  }

  /**
   * Entfernt das Versandziel. Das Backend lehnt das Löschen ab, sobald
   * Sendungen daran hängen — die Meldung landet dann im State.
   */
  async deleteConfig(): Promise<boolean> {
    const existing = this.initialConfig();
    if (!existing || this.saving()) return false;

    this.saving.set(true);
    this.errorMessage.set(null);
    try {
      await this.configService.deleteConfigById(this.operationId(), existing.id);
      return true;
    } catch (err) {
      this.errorMessage.set(serverErrorMessage(err));
      return false;
    } finally {
      this.saving.set(false);
    }
  }

  /** Legt die Config an bzw. speichert sie. null = Fehler (Meldung steht im State). */
  async save(): Promise<ShippingConfig | null> {
    if (!this.canSave() || this.saving()) return null;

    this.saving.set(true);
    this.errorMessage.set(null);
    const payload: ShippingConfigPayload = this.buildFormData();
    const existing = this.initialConfig();
    try {
      return existing
        ? await this.configService.updateConfigById(this.operationId(), existing.id, payload)
        : await this.configService.createAdditionalConfig(this.operationId(), payload);
    } catch (err) {
      this.errorMessage.set(serverErrorMessage(err));
      return null;
    } finally {
      this.saving.set(false);
    }
  }
}

/** Server-Fehlermeldung aus einer fehlgeschlagenen Antwort ziehen. */
function serverErrorMessage(err: unknown): string {
  const body = (err as { error?: { error?: string } } | undefined)?.error;
  return body?.error || 'Speichern fehlgeschlagen — bitte erneut versuchen.';
}
